import React, { useState, useEffect } from "react";
import { PageTitle } from "../../components";
import {
  Table,
  Space,
  Button,
  Form,
  Input,
  Select,
  Modal,
  Popconfirm,
  message,
  notification
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { Link, Outlet } from "react-router-dom";
import dayjs from "dayjs";
import type { ColumnsType } from "antd/lib/table";
import { useSelector } from "react-redux";
import { CURRENT_CLUB_ID } from "../../_helpers/constants";
import { usersApi, teamsApi, userClubsApi } from "../../_helpers/apiService";

interface DataType {
  key: string;
  name: string;
  date: string;
}

const formItemLayout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 14 }
};

const tailLayout = {
  wrapperCol: { offset: 6, span: 18 }
};

// Columns defined inside component to access teams state

const deleteReferee = async (referee: any) => {
  // await deleteDoc(doc(db, "referees", referee.key));
  // message.success(
  //   `"${referee.lastName} ${referee.firstName}" шүүгч амжилттай устгалаа.`
  // );
};

const { Option } = Select;

function Leaders() {
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  const [data, setData] = useState<DataType[]>([]);
  const [oldData, setOldData] = useState<DataType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchKey, setSearchKey] = useState("last_name");
  const [searchValue, setSearchValue] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [teams, setTeams] = useState<any>({});
  const [archers, setArchers] = useState<any[]>([]);
  const [filteredArchers, setFilteredArchers] = useState<any[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);
  const userData = useSelector((state: any) => state.authentication.userData);

  const columns: ColumnsType<DataType> = [
    {
      title: "№",
      dataIndex: "id",
      key: "id",
      width: 80,
      render: (text, row, index) => <>{index + 1}</>
    },
    {
      title: "Овог",
      dataIndex: "last_name",
      key: "last_name",
      width: 120,
    },
    {
      title: "Нэр",
      dataIndex: "first_name",
      key: "first_name",
      width: 120,
    },
    {
      title: "Баг",
      dataIndex: "team_id",
      key: "team_id",
      width: 150,
      render: (teamId: number) => {
        const team = teams[teamId];
        return team ? team.name : '-';
      }
    },
    {
      title: "Имэйл хаяг",
      dataIndex: "email",
      key: "email",
      width: 200,
    },
    {
      title: "Утасны дугаар",
      dataIndex: "phone_number",
      key: "phone_number",
      width: 130,
      render: (text: string) => text?.replace(/^\+976/, '') || '',
    },
    {
      title: "Үйлдэл",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Space size="middle">
          <Link to={`/leaders/${record.key}`} key={record.key}>
            Харах
          </Link>
        </Space>
      )
    }
  ];

  useEffect(() => {
    fetchLeaders();
    fetchTeams();
    fetchArchers();
  }, []);

  const fetchTeams = async () => {
    try {
      const response = await teamsApi.getAll({
        club_id: CURRENT_CLUB_ID,
        limit: 1000
      });

      const teamsObj: any = {};
      response.data.items.forEach((team: any) => {
        teamsObj[team.id] = team;
      });
      setTeams(teamsObj);
    } catch (error: any) {
      console.error("Fetch teams error:", error);
      message.error("Багуудын мэдээлэл татахад алдаа гарлаа");
    }
  };

  const fetchArchers = async () => {
    try {
      const response = await userClubsApi.getWithDetails(CURRENT_CLUB_ID, {
        role: 'archer',
        limit: 1000
      });

      setArchers(response.data.items);
    } catch (error: any) {
      console.error("Fetch archers error:", error);
      message.error("Харваачдын мэдээлэл татахад алдаа гарлаа");
    }
  };

  const handleTeamChange = (teamId: number) => {
    setSelectedTeamId(teamId);
    form.setFieldValue('archerId', undefined);

    if (teamId) {
      const filtered = archers.filter((archer: any) => archer.team_id === teamId);
      setFilteredArchers(filtered);
    } else {
      setFilteredArchers([]);
    }
  };

  const fetchLeaders = async () => {
    setLoading(true);
    try {
      // Fetch users who are team leaders in this club
      const response = await userClubsApi.getWithDetails(CURRENT_CLUB_ID, {
        is_team_leader: true,
        limit: 1000
      });

      const leaders = response.data.items.map((leader: any) => ({
        ...leader,
        id: leader.user_id,
        key: leader.user_id.toString(),
      }));

      setData(leaders);
      setOldData(leaders);
      setLoading(false);
    } catch (error: any) {
      console.error("Fetch leaders error:", error);
      message.error("Багийн ахлагчдын мэдээлэл татахад алдаа гарлаа");
      setLoading(false);
    }
  };

  const onFinish = () => {
    setLoading(true);
    // Filter locally for simplicity
    const filtered = oldData.filter((item: any) => {
      const value = item[searchKey];
      return value && value.toString().toLowerCase().includes(searchValue?.toLowerCase() || '');
    });
    setData(filtered);
    setLoading(false);
  };

  const handleCancel = () => {
    setVisible(false);
    form.resetFields();
    setSelectedTeamId(null);
    setFilteredArchers([]);
  };

  const handleOk = () => {
    setConfirmLoading(true);

    form
      .validateFields()
      .then(async (values) => {
        try {
          const selectedArcher = archers.find((a: any) => a.user_id === values.archerId);

          if (!selectedArcher) {
            throw new Error("Харваач олдсонгүй");
          }

          // Update user role to 'leader'
          await usersApi.update(values.archerId, {
            role: 'leader'
          });

          // Get the user_club record and update is_team_leader
          const userClubResponse = await userClubsApi.getAll({
            club_id: CURRENT_CLUB_ID,
            user_id: values.archerId
          });

          if (userClubResponse.data.items.length > 0) {
            const userClub = userClubResponse.data.items[0];
            await userClubsApi.update(userClub.id, {
              team_id: values.teamId,
              is_team_leader: true
            });
          }

          // Update team's leader_id
          await teamsApi.update(values.teamId, {
            leader_id: values.archerId
          });

          setVisible(false);
          form.resetFields();
          setSelectedTeamId(null);
          setFilteredArchers([]);
          setConfirmLoading(false);
          message.success(
            `"${selectedArcher.last_name} ${selectedArcher.first_name}" багийн ахлагчаар амжилттай томилогдлоо.`
          );
          fetchLeaders(); // Refresh the list
          fetchArchers(); // Refresh archers list
        } catch (error: any) {
          setConfirmLoading(false);
          console.error("Create leader error:", error);
          const errorMessage = error.response?.data?.detail || "Багийн ахлагч нэмэхэд алдаа гарлаа";
          message.error(errorMessage);
        }
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
        setConfirmLoading(false);
      });
  };

  const clearSearchForm = () => {
    searchForm.resetFields();
    setSearchValue(null);
    setData(oldData);
  };

  return (
    <>
      <PageTitle
        title="Багийн ахлагчид"
        description="Багийн ахлагчдын жагсаалт"
        right={
          // userData.role === "leader" ? (
          //   <> </>
          // ) : (
          <Button
            shape="round"
            onClick={() => setVisible(true)}
            icon={<PlusOutlined />}
            size="large"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center"
            }}
          >
            Багийн ахлагч нэмэх
          </Button>
          // )
        }
      />
      <div className="page-content-wrapper">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="card">
                <div className="card-body">
                  <div className="row">
                    <div className="col-lg-12">
                      <Form
                        layout="inline"
                        style={{ marginBottom: 10 }}
                        form={searchForm}
                        initialValues={{ type: searchKey }}
                      >
                        <Form.Item name="type" label="Талбар">
                          <Select
                            value={searchKey}
                            style={{ width: 300 }}
                            onChange={(val) => setSearchKey(val)}
                            allowClear
                          >
                            <Option value="last_name">Овогоор</Option>
                            <Option value="first_name">Нэрээр</Option>
                            <Option value="email">Имэйл хаягаар</Option>
                          </Select>
                        </Form.Item>
                        <Form.Item
                          name="name"
                          label="Хайх утга"
                          rules={[
                            {
                              required: true,
                              message: "Хайх утга оруулна уу!"
                            }
                          ]}
                        >
                          <Input
                            onChange={(e: any) =>
                              setSearchValue(e.target.value)
                            }
                          />
                        </Form.Item>
                        <Form.Item {...tailLayout}>
                          <Button type="primary" onClick={() => onFinish()}>
                            Хайх
                          </Button>
                        </Form.Item>
                        <Form.Item {...tailLayout}>
                          <Button
                            type="dashed"
                            onClick={() => clearSearchForm()}
                          >
                            Арилгах
                          </Button>
                        </Form.Item>
                      </Form>
                    </div>
                  </div>
                  <div className="table-responsive">
                    <Table
                      columns={columns}
                      dataSource={data}
                      loading={loading}
                      scroll={{ x: 'max-content' }}
                    />
                  </div>

                  {/* <Pagination
                    current={currentPage}
                    totalPages={totalPages}
                    prevAction={prevPage}
                    nextAction={nextPage}
                    changeAction={(num) => changePage(num)}
                  /> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        title="Багийн ахлагч нэмэх"
        open={visible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText="Нэмэх"
        cancelText="Болих"
        width={720}
        confirmLoading={confirmLoading}
      >
        <Form {...formItemLayout} form={form}>
          <Form.Item
            label="Баг"
            name="teamId"
            rules={[{ required: true, message: "Баг сонгоно уу!" }]}
          >
            <Select
              placeholder="Баг сонгоно уу"
              allowClear
              onChange={handleTeamChange}
              showSearch
              optionFilterProp="children"
            >
              {Object.values(teams)
                .sort((a: any, b: any) => (a.name > b.name ? 1 : -1))
                .map((team: any) => (
                  <Option key={team.id} value={team.id}>
                    {team.name}
                  </Option>
                ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="Харваач"
            name="archerId"
            rules={[{ required: true, message: "Харваач сонгоно уу!" }]}
          >
            <Select
              placeholder={selectedTeamId ? "Харваач сонгоно уу" : "Эхлээд баг сонгоно уу"}
              allowClear
              disabled={!selectedTeamId}
              showSearch
              optionFilterProp="children"
            >
              {filteredArchers
                .sort((a: any, b: any) => (a.last_name > b.last_name ? 1 : -1))
                .map((archer: any) => (
                  <Option key={archer.user_id} value={archer.user_id}>
                    {archer.last_name} {archer.first_name}
                  </Option>
                ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
export default Leaders;
